import type { ReadonlySignal } from '@preact/signals-core';
import type { AppLocation, NavigationTarget } from 'src/lib/navigation/types';

export type RouteParams = Readonly<Record<string, string | undefined>>;

export interface RouteEnterContext<TContainer> {
    readonly container: TContainer;
    readonly location: AppLocation;
    readonly params: RouteParams;
    readonly signal: AbortSignal;
    /**
     * Resolves when this entry's navigation commits; rejects with an AbortError if it is abandoned
     * instead. Effects an entry starts that must not run before commit (such as a navigation of
     * their own, which would supersede this one) wait for it.
     */
    readonly committed: Promise<void>;
    register(disposable: Disposable): void;
}

export interface RouteDefinition<TContainer, TLoaded> {
    readonly id: string;
    readonly path?: string;
    readonly index?: boolean;
    readonly enter?: (context: RouteEnterContext<TContainer>) => void | Promise<void>;
    readonly load?: () => TLoaded | Promise<TLoaded>;
    readonly children?: readonly RouteDefinition<TContainer, TLoaded>[];
}

export interface RouteMatch<TContainer, TLoaded> {
    readonly definition: RouteDefinition<TContainer, TLoaded>;
    readonly params: RouteParams;
    readonly pathnameBase: string;
    readonly loaded: TLoaded | null;
}

export interface RouteSnapshot<TContainer, TLoaded> {
    readonly location: AppLocation;
    readonly matches: readonly RouteMatch<TContainer, TLoaded>[];
    readonly params: RouteParams;
}

export interface PreparedRoute<TContainer, TLoaded> extends Disposable {
    readonly location: AppLocation;
    readonly replace: boolean;
    readonly snapshot: RouteSnapshot<TContainer, TLoaded>;
    commit(location?: AppLocation): void;
}

export interface RouteCoreContract<TContainer, TLoaded> extends Disposable {
    readonly current: ReadonlySignal<RouteSnapshot<TContainer, TLoaded> | null>;
    readonly pendingLocation: ReadonlySignal<AppLocation | null>;
    prepare(location: AppLocation, signal: AbortSignal): Promise<PreparedRoute<TContainer, TLoaded>>;
}

export class RouteRedirect {
    constructor(
        readonly to: NavigationTarget,
        readonly replace = true,
    ) {}
}

export const redirect = (to: NavigationTarget, options: { readonly replace?: boolean } = {}): never => {
    throw new RouteRedirect(to, options.replace ?? true);
};

export const defineRoute = <TContainer, TLoaded>(
    definition: RouteDefinition<TContainer, TLoaded>,
): RouteDefinition<TContainer, TLoaded> => definition;
